import { useMemo } from "react";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface UseHeartRateChartConfigProps {
  selectedRange: TimeRange;
  navigationOffset: number;
  selectedDate?: Date;
  lowChartData: ChartDataPoint[];
  highChartData: ChartDataPoint[];
}

interface ChartConfig {
  scale: { x: "time" | "linear" };
  domain: {
    x: [Date, Date] | [number, number];
    y: [number, number];
  };
  tickValues: (Date | number)[];
  tickFormat: (x: Date | number) => string;
  yTickValues?: number[]; // Add Y-axis tick values
}

export const useHeartRateChartConfig = ({
  selectedRange,
  navigationOffset,
  selectedDate,
  lowChartData,
  highChartData,
}: UseHeartRateChartConfigProps): ChartConfig => {
  const chartConfig = useMemo(() => {
    const today = new Date();

    // Calculate domain based on actual data if available
    let dataDomain = null;
    let dataYear = today.getFullYear(); // Default to current year

    // Calculate dynamic Y domain based on actual heart rate data
    let yDomain: [number, number] = [0, 200]; // Reasonable heart rate range (BPM)
    let yTickValues: number[] = [0, 40, 80, 120, 160, 200]; // Reasonable heart rate tick values

    if (lowChartData.length > 0) {
      // Combine both low and high data to get the full range
      const allYValues = [
        ...lowChartData.map((point) => point.y),
        ...highChartData.map((point) => point.y),
      ];
      const minY = Math.min(...allYValues);
      const maxY = Math.max(...allYValues);

      // For heart rate, ensure reasonable range that adapts to data
      if (allYValues.length === 1) {
        // Single data point: create range around it
        const value = allYValues[0];
        const range = 40; // 40 BPM range around the value
        yDomain = [
          Math.max(0, Math.floor(value - range / 2)), // Minimum 0 BPM
          Math.min(300, Math.ceil(value + range / 2)), // Maximum 300 BPM
        ];
      } else {
        // Multiple data points: add more generous padding to ensure all data is visible
        const dataRange = maxY - minY;
        const yPadding = Math.max(20, dataRange * 0.25); // At least 20 BPM padding, or 25% of data range
        yDomain = [
          Math.max(0, Math.floor(minY - yPadding)), // Minimum 0 BPM (lowered from 20)
          Math.min(300, Math.ceil(maxY + yPadding)), // Maximum 300 BPM (increased from 250)
        ];
      }

      // Ensure Y-axis domain is consistent across all time ranges
      // Force minimum range of 40 BPM to prevent tiny ranges and ensure visibility
      const currentRange = yDomain[1] - yDomain[0];
      if (currentRange < 40) {
        const center = (yDomain[0] + yDomain[1]) / 2;
        yDomain = [
          Math.max(0, Math.floor(center - 20)),
          Math.min(300, Math.ceil(center + 20)),
        ];
      }

      // Generate reasonable tick values for heart rate
      const range = yDomain[1] - yDomain[0];
      const step = Math.max(5, Math.ceil(range / 8)); // At least 5 BPM between ticks, aim for 8+ ticks
      yTickValues = [];

      // Add safety check to prevent infinite loops
      const maxIterations = 30;
      let iterations = 0;

      for (
        let i = yDomain[0];
        i <= yDomain[1] && iterations < maxIterations;
        i += step
      ) {
        yTickValues.push(i);
        iterations++;
      }

      // Ensure we have at least the min and max values
      if (!yTickValues.includes(yDomain[0])) yTickValues.unshift(yDomain[0]);
      if (!yTickValues.includes(yDomain[1])) yTickValues.push(yDomain[1]);

      // If we still don't have enough values, add more intermediate ones
      if (yTickValues.length < 6) {
        const stepSize = Math.max(2, Math.floor(range / 6));
        yTickValues = [];
        for (let i = yDomain[0]; i <= yDomain[1]; i += stepSize) {
          yTickValues.push(i);
        }
        // Ensure we have the max value
        if (!yTickValues.includes(yDomain[1])) {
          yTickValues.push(yDomain[1]);
        }
      }

      // Sort tick values to ensure proper order
      yTickValues.sort((a, b) => a - b);
    }

    // Safely handle xValues calculation
    if (lowChartData.length > 0) {
      const xValues = lowChartData.map((point) => point.x);
      const xTimes = xValues.map((x) =>
        typeof x === "number" ? x : x.getTime(),
      );

      // Check for valid numbers
      const validTimes = xTimes.filter(
        (time) => !isNaN(time) && isFinite(time),
      );

      if (validTimes.length > 0) {
        const minX = Math.min(...validTimes);
        const maxX = Math.max(...validTimes);
        dataDomain = { min: minX, max: maxX };
      }
    }

    // Get the year from the actual data (using local timezone)
    if (dataDomain) {
      const firstDataPoint = new Date(dataDomain.min);
      dataYear = firstDataPoint.getFullYear();
    }

    switch (selectedRange) {
      case "1d": {
        // Show every 2 hours: 12am, 2am, 4am, 6am, 8am, 10am, 12pm, 2pm, 4pm, 6pm, 8pm, 10pm
        // Use local timezone for day view

        return {
          scale: { x: "linear" as const },
          domain: {
            x: [0, 23] as [number, number],
            y: yDomain,
          },
          tickValues: [0, 3, 6, 9, 12, 15, 18, 21],
          tickFormat: (x: Date | number) => {
            return String(x);
          },
          yTickValues,
        };
      }
      case "7d": {
        // Use selectedDate if provided, otherwise fall back to current date + navigation offset
        let baseDate: Date;

        if (selectedDate) {
          baseDate = new Date(selectedDate);
        } else {
          // Fallback to current date + navigation offset
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth();
          const currentDate = today.getDate();
          baseDate = new Date(currentYear, currentMonth, currentDate);
          baseDate.setDate(baseDate.getDate() + navigationOffset);
        }

        const targetYear = baseDate.getFullYear();
        const targetMonth = baseDate.getMonth();
        const targetDate = baseDate.getDate();
        const targetDay = baseDate.getDay();

        // Calculate week start (Monday) and end (Sunday) in local timezone
        const weekStart = new Date(
          targetYear,
          targetMonth,
          targetDate - targetDay + 1,
        ); // Monday
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday
        weekEnd.setHours(0, 0, 0, 0);

        const weekTicks = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i); // Mon to Sun
          // Set to noon to avoid timezone edge cases
          date.setHours(0, 0, 0, 0);
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [weekStart, weekEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: weekTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { weekday: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1m": {
        // Use selectedDate if provided, otherwise fall back to current date + navigation offset
        let baseDate: Date;

        if (selectedDate) {
          baseDate = new Date(selectedDate);
        } else {
          // Fallback to current date + navigation offset
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth();
          const currentDate = today.getDate();
          baseDate = new Date(currentYear, currentMonth, currentDate);
          baseDate.setDate(baseDate.getDate() + navigationOffset);
        }

        const targetYear = baseDate.getFullYear();
        const targetMonth = baseDate.getMonth();

        const monthStart = new Date(targetYear, targetMonth, 1, 0, 0, 0, 0); // 1st of target month
        const monthEnd = new Date(targetYear, targetMonth + 1, 0, 0, 0, 0, 0); // Last day of target month

        // Create ticks for every 2 days
        const daysInMonth = monthEnd.getDate();
        const tickCount = Math.min(Math.ceil(daysInMonth / 2), 31); // Cap at 31 to prevent huge arrays
        const monthTicks = Array.from(
          { length: Math.max(1, tickCount) },
          (_, i) => {
            const day = Math.min(Math.floor(i * 2) + 1, daysInMonth);
            const date = new Date(targetYear, targetMonth, day, 0, 0, 0, 0);
            return date;
          },
        );

        return {
          scale: { x: "time" as const },
          domain: {
            x: [monthStart, monthEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: monthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.getDate().toString();
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "6m": {
        // Use selectedDate if provided, otherwise fall back to current date logic
        let targetYear: number;
        let isFirstHalf: boolean;

        if (selectedDate) {
          targetYear = selectedDate.getFullYear();
          isFirstHalf = selectedDate.getMonth() <= 5; // Jan-Jun (0-5) or Jul-Dec (6-11)
        } else {
          // Fallback to current date logic
          targetYear = today.getFullYear();
          const currentMonth = today.getMonth();
          isFirstHalf = currentMonth <= 5;
        }

        const sixMonthTicks = Array.from({ length: 6 }, (_, i) => {
          const monthIndex = isFirstHalf ? i : i + 6; // 0-5 for Jan-Jun, 6-11 for Jul-Dec
          const date = new Date(targetYear, monthIndex, 1, 0, 0, 0); // 1st of each month
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [
              new Date(targetYear, isFirstHalf ? 0 : 6, 1, 0, 0, 0), // Start of first month (Jan or Jul)
              new Date(targetYear, isFirstHalf ? 5 : 11, 1, 0, 0, 0), // End of last month (Jun or Dec)
            ] as [Date, Date],
            y: yDomain,
          },
          tickValues: sixMonthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1y": {
        // Use selectedDate if provided, otherwise fall back to data-based logic
        let targetYear: number;

        if (selectedDate) {
          targetYear = selectedDate.getFullYear();
        } else if (dataDomain) {
          // Use actual data domain for year view
          targetYear = dataYear;
        } else {
          // Fallback to current year
          targetYear = dataYear;
        }

        // Show all 12 months of the target year
        const yearTicks = [
          new Date(targetYear, 0, 1, 0, 0, 0), // Jan
          new Date(targetYear, 1, 1, 0, 0, 0), // Feb
          new Date(targetYear, 2, 1, 0, 0, 0), // Mar
          new Date(targetYear, 3, 1, 0, 0, 0), // Apr
          new Date(targetYear, 4, 1, 0, 0, 0), // May
          new Date(targetYear, 5, 1, 0, 0, 0), // Jun
          new Date(targetYear, 6, 1, 0, 0, 0), // Jul
          new Date(targetYear, 7, 1, 0, 0, 0), // Aug
          new Date(targetYear, 8, 1, 0, 0, 0), // Sep
          new Date(targetYear, 9, 1, 0, 0, 0), // Oct
          new Date(targetYear, 10, 1, 0, 0, 0), // Nov
          new Date(targetYear, 11, 1, 0, 0, 0), // Dec
        ];

        return {
          scale: { x: "time" as const },
          domain: {
            x: [new Date(targetYear, 0, 1), new Date(targetYear, 11, 1)] as [
              Date,
              Date,
            ],
            y: yDomain,
          },
          tickValues: yearTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      default: {
        // Default to week view - always use predefined ticks
        const defaultTicks = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(today);
          date.setDate(date.getDate() - (6 - i));
          return date;
        });
        return {
          scale: { x: "time" as const },
          domain: dataDomain
            ? {
                x: [new Date(dataDomain.min), new Date(dataDomain.max)] as [
                  Date,
                  Date,
                ],
                y: yDomain,
              }
            : { y: yDomain },
          tickValues: defaultTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { weekday: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
    }
  }, [
    selectedRange,
    navigationOffset,
    selectedDate,
    lowChartData,
    highChartData,
  ]);

  // Ensure domain.x is always defined to satisfy ChartConfig type
  if (!chartConfig.domain.x) {
    const now = new Date();
    chartConfig.domain = {
      ...chartConfig.domain,
      x: [now, now] as [Date, Date],
    };
  }

  // Final consistency check - ensure Y domain is reasonable for heart rate
  if (
    chartConfig.domain.y &&
    chartConfig.domain.y[1] - chartConfig.domain.y[0] < 20
  ) {
    const center = (chartConfig.domain.y[0] + chartConfig.domain.y[1]) / 2;
    chartConfig.domain.y = [
      Math.max(30, Math.floor(center - 10)),
      Math.min(250, Math.ceil(center + 10)),
    ];
  }

  return chartConfig as ChartConfig;
};
